import React, { type ChangeEvent, type ReactElement, useEffect, useRef, useState } from 'react'
import { Chip, CircularProgress, Typography, useTheme } from '@mui/material'
import { createFileUrl, uploadFile } from 'common/files/files.service'
import { setErrorMessage } from 'store/app.slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import IconButton from 'common/ui/icon-button'
import { BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import ThemeButton from 'common/ui/theme-button'
import { ReactComponent as PlusIcon } from 'assets/plusIcon.svg'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import { getFileArray } from 'utils/functions'
import { sendVerificationRequest } from 'features/authentication/auth.service'

import { initializeVendor, setAdditionalDocuments } from '../../vendor.service'
import { VendorStatus } from '../../vendor.types'

import styles from './AdditionalDocuments.module.scss'

const AdditionalDocuments: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState<GeneratedFile[]>([])
  const { vendor } = useAppSelector((state) => state.vendor)
  const { typography, palette } = useTheme()

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    ;(async () => {
      setLoading(true)
      const uploadedFiles = event.target.files ? getFileArray(event.target.files) : []
      try {
        const fileData = await Promise.all(
          uploadedFiles.map(async (file) => {
            const generatedFile = await createFileUrl({
              type: GeneratedFileType.VendorOtherDoc,
              mode: 'rw',
              multiple: true,
              data: { filename: file.name, vendorId: vendor?.id },
            })
            await uploadFile(generatedFile, file)
            return generatedFile
          }),
        )
        setFiles(fileData)
      } catch (e) {
        console.error('error uploading files: ', e)
        dispatch(setErrorMessage('Error uploading files'))
      } finally {
        event.target.value = ''
        setLoading(false)
      }
    })().catch((error) => {
      console.error('An error occurred:', error)
    })
  }

  const handleDeleteRefDoc = (file: GeneratedFile, index: number): void => {
    const existingDocuments = [...(vendor?.additionalDocuments?.documents ?? [])]
    existingDocuments.splice(index, 1)
    void dispatch(setAdditionalDocuments({ documents: existingDocuments }))
  }

  useEffect(() => {
    const combinedDocuments = [...(vendor?.additionalDocuments?.documents ?? []), ...files].slice(0, 4)
    void dispatch(setAdditionalDocuments({ documents: combinedDocuments }))
  }, [files])

  const sendVerificationRequestHandler = (): void => {
    void dispatch(sendVerificationRequest()).then(() => dispatch(initializeVendor()))
  }

  return (
    <div className={styles.fileWrapper}>
      <div className={styles.title}>
        <Typography variant="title3">Dodajte dokumenta</Typography>
        {vendor?.additionalDocuments?.documents.length !== 0 &&
          vendor?.status === VendorStatus.additional_documents && (
            <Typography variant="title3" sx={{ color: palette?.gray.main }}>
              Dodato {vendor?.additionalDocuments?.documents.length}/4 dokumenta
            </Typography>
          )}
      </div>
      <div className={styles.row}>
        <div
          className={`${styles.filesPlaceholder} ${
            vendor?.status === VendorStatus.additional_documents ? styles.filesPlaceholderWidth : ''
          } `}
        >
          <div className={styles.fileChipsWrapper}>
            {vendor?.additionalDocuments?.documents.map((f, i) => (
              <Chip
                key={i}
                label={f.filename}
                deleteIcon={
                  vendor?.status === VendorStatus.additional_documents ? (
                    <ClearIcon stroke={'white'} />
                  ) : (
                    <></>
                  )
                }
                onDelete={() => {
                  handleDeleteRefDoc(f, i)
                }}
                className={styles.chip}
                sx={{
                  '& .MuiChip-label': {
                    width: 'calc(100% - 24px)',
                    ...typography.title3,
                  },
                }}
              />
            ))}
            {vendor?.additionalDocuments?.documents.length === 0 && (
              <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                Možete maksimalno 4 dokumenta da okačite
              </Typography>
            )}
          </div>
          {(!vendor?.additionalDocuments ||
            (vendor?.additionalDocuments?.documents.length < 4 &&
              vendor?.status === VendorStatus.additional_documents)) && (
            <label htmlFor="files">
              <IconButton
                icon={loading ? <CircularProgress size={16} /> : <PlusIcon />}
                onClick={() => {
                  fileInputRef?.current?.click()
                }}
                variant={BUTTON_VARIANTS.secondary}
              />
              <input
                type="file"
                id="files"
                hidden
                accept=".pdf"
                multiple={true}
                onChange={handleFileSelect}
                ref={fileInputRef}
              />
            </label>
          )}
        </div>
        <div>
          {!!vendor?.additionalDocuments?.documents.length &&
            vendor.status === VendorStatus.additional_documents && (
              <ThemeButton
                sx={{
                  padding: 0,
                  width: '130px',
                  backgroundColor: palette.charts.green200.main,
                  '&:hover': {
                    backgroundColor: palette.charts.green200.main,
                  },
                }}
                onClick={sendVerificationRequestHandler}
              >
                POŠALJI
              </ThemeButton>
            )}
        </div>
      </div>
    </div>
  )
}

export default AdditionalDocuments
